import React, { useState, useEffect, useMemo } from 'react';
import { dayjs } from 'src/libs/dayjs';
import {
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  Box,
  Text,
  FormControl,
  FormLabel,
  Select,
  HStack,
  Input,
  Textarea,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Switch } from 'src/components/atoms/Switch';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useUpdateGoalMutation } from 'src/api/goals';
import {
  useModelVersionsQuery,
  useCampaignsQuery,
  useMediaQuery,
} from 'src/api/queries';
import { useAppContext } from 'src/contexts/AppContext';
import {
  baseModelName,
  filterQuickWhenFullExists,
  isQuickVersion,
} from 'src/contexts/FilterContext/useModelVersions';
import { AppServices } from 'src/app/routes/utils';
import { stripFullModelSuffix } from 'src/utils/model';
import {
  GoalDetail,
  CreateGoalRequest,
  UpdateGoalRequest,
  ModelType,
  Metric,
} from 'src/features/Goals/types';
import {
  PERFORMANCE_METRIC_OPTIONS,
  MODEL_TYPE_OPTIONS,
} from 'src/features/Goals/utils/formatters';
import { snakeToTitleCase } from 'src/utils/String';
import { MentionedUsersSelect } from '../MentionedUsersSelect';
import { MediaChannelsSelect } from '../MediaChannelsSelect';
import { CampaignsMultiSelect } from '../CampaignsMultiSelect';
import { GoalDateRangePicker } from '../GoalDateRangePicker';

type GoalDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  // Create mode
  onCreateSubmit: (data: CreateGoalRequest) => void;
  isCreateLoading: boolean;
  // Edit mode
  editingGoal?: GoalDetail | null;
  onUpdateSuccess: () => void;
  onUpdateError: (message?: string) => void;
  // Data
  customer: string;
  campaignGroups: { id: string; name: string }[];
  users: { id: string; name: string; avatarUrl?: string }[];
};

const inputStyles = {
  bg: 'surface.panel',
  border: '1px solid',
  borderColor: 'surface.border',
  borderRadius: 'md',
  fontSize: '16px',
  fontFamily: 'marketingPage',
  _placeholder: { color: 'content.muted' },
  _focus: {
    borderColor: 'icon.green',
    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
  },
};

const labelStyles = {
  fontFamily: 'marketingPage',
  fontSize: '16px',
  fontWeight: 500,
  color: 'content.title',
  mb: 3,
};

export const GoalDrawer: React.FC<GoalDrawerProps> = ({
  isOpen,
  onClose,
  onCreateSubmit,
  isCreateLoading,
  editingGoal = null,
  onUpdateSuccess,
  onUpdateError,
  customer,
  campaignGroups,
  users,
}) => {
  const { currentCustomer } = useAppContext();
  const isEditing = !!editingGoal;

  // Form state
  const [title, setTitle] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelType, setModelType] = useState<ModelType | ''>('');
  const [defineCampaignsManually, setDefineCampaignsManually] = useState(false);
  const [campaignGroupId, setCampaignGroupId] = useState('');
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(
    new Set(),
  );
  const [metric, setMetric] = useState<Metric | ''>('');
  const [targetValue, setTargetValue] = useState('');
  const [selectedMediaChannels, setSelectedMediaChannels] = useState<
    Set<string>
  >(new Set());
  const [goalPeriodStart, setGoalPeriodStart] = useState<Date | null>(null);
  const [goalPeriodEnd, setGoalPeriodEnd] = useState<Date | null>(null);
  const [mentionedUsers, setMentionedUsers] = useState<Set<string>>(new Set());
  const [description, setDescription] = useState('');

  // Fetch model versions for all model types
  const customerName = currentCustomer?.customerName ?? '';
  const { data: mmModels = [] } = useModelVersionsQuery(
    { customer: customerName, model_type: AppServices.MM },
    Boolean(customerName),
  );
  const { data: daModels = [] } = useModelVersionsQuery(
    { customer: customerName, model_type: AppServices.DA },
    Boolean(customerName),
  );
  const { data: moModels = [] } = useModelVersionsQuery(
    { customer: customerName, model_type: AppServices.MO },
    Boolean(customerName),
  );

  // Combine all model versions
  const allModelVersions = useMemo(() => {
    const combined = [
      ...mmModels.map((m) => ({ ...m, modelType: m.modelType as ModelType })),
      ...daModels.map((m) => ({ ...m, modelType: m.modelType as ModelType })),
      ...moModels.map((m) => ({ ...m, modelType: m.modelType as ModelType })),
    ];

    // Deduplicate by modelParametersId
    const seen = new Set<string>();
    const unique = combined.filter((m) => {
      if (seen.has(m.modelParametersId)) return false;
      seen.add(m.modelParametersId);
      return true;
    });

    return filterQuickWhenFullExists(unique);
  }, [mmModels, daModels, moModels]);

  // Get selected model details for fetching campaigns
  const selectedModel = useMemo(() => {
    return allModelVersions.find(
      (m) => m.modelParametersId === selectedModelId,
    );
  }, [allModelVersions, selectedModelId]);

  const selectableModelVersions = useMemo(
    () => allModelVersions.filter((m) => !isQuickVersion(m)),
    [allModelVersions],
  );

  // Metric options: the selected model's KPI metric (outcome) + static
  // performance metrics. Legacy goals may carry a metric that's no longer
  // offered — keep it selectable so editing doesn't silently clear it.
  const metricOptions = useMemo(() => {
    const options: { value: Metric; label: string }[] = [];
    if (selectedModel) {
      const kpiMetric = selectedModel.kpiMetric;
      options.push({
        value: kpiMetric ?? 'sales',
        label: kpiMetric
          ? `Incremental ${snakeToTitleCase(kpiMetric)}`
          : 'Model KPI',
      });
    }
    options.push(...PERFORMANCE_METRIC_OPTIONS);
    if (
      editingGoal &&
      !options.some((opt) => opt.value === editingGoal.metric)
    ) {
      options.unshift({
        value: editingGoal.metric,
        label: `Incremental ${snakeToTitleCase(editingGoal.metric)}`,
      });
    }
    return options;
  }, [selectedModel, editingGoal]);

  // Fetch campaigns based on selected model
  const { data: modelCampaigns = [] } = useCampaignsQuery(
    {
      customer: customerName,
      model_build: selectedModel?.buildID ?? '',
      from: selectedModel?.dataStartDate ?? '',
      to: selectedModel?.dataEndDate ?? '',
    },
    Boolean(selectedModel?.buildID),
  );

  // Transform campaigns to format expected by CampaignsMultiSelect
  const campaignsForSelect = useMemo(
    () =>
      modelCampaigns.map((c) => ({
        id: Number(c.id),
        name: c.name,
        category: c.tags?.[0]?.tagGroup ?? 'Uncategorized',
      })),
    [modelCampaigns],
  );

  // Fetch media channels based on selected model
  const { data: modelMedia = [] } = useMediaQuery(
    {
      customer: customerName,
      model_build: selectedModel?.buildID ?? '',
    },
    Boolean(selectedModel?.buildID),
  );

  // Handle model selection - auto-set model type
  const handleModelSelect = (modelParametersId: string) => {
    setSelectedModelId(modelParametersId);
    // Reset campaigns, media channels and metric (the outcome option is
    // model-specific — it's the model's own KPI metric)
    setSelectedCampaigns(new Set());
    setSelectedMediaChannels(new Set());
    setMetric('');
    if (!modelParametersId) {
      setModelType('');
      return;
    }
    const model = allModelVersions.find(
      (m) => m.modelParametersId === modelParametersId,
    );
    if (model) {
      setModelType(model.modelType);
    }
  };

  const { toastState, showError, hideToast } = useCustomToast();

  // Update mutation
  const updateGoalMutation = useUpdateGoalMutation(
    customer,
    editingGoal?.id ?? '',
  );

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      if (editingGoal) {
        setTitle(editingGoal.title);
        setSelectedModelId(editingGoal.modelParametersId);
        setModelType(editingGoal.modelType);
        setDefineCampaignsManually(editingGoal.defineCampaignsManually);
        setCampaignGroupId(editingGoal.campaignGroup?.id ?? '');
        setSelectedCampaigns(new Set(editingGoal.campaigns.map((c) => c.id)));
        setMetric(editingGoal.metric);
        setTargetValue(String(editingGoal.targetValue));
        setSelectedMediaChannels(new Set(editingGoal.mediaChannels));
        setGoalPeriodStart(new Date(editingGoal.goalPeriodStart));
        setGoalPeriodEnd(new Date(editingGoal.goalPeriodEnd));
        setMentionedUsers(new Set(editingGoal.mentionedUsers.map((u) => u.id)));
        setDescription(editingGoal.description ?? '');
      } else {
        setTitle('');
        setSelectedModelId('');
        setModelType('');
        setDefineCampaignsManually(false);
        setCampaignGroupId('');
        setSelectedCampaigns(new Set());
        setMetric('');
        setTargetValue('');
        setSelectedMediaChannels(new Set());
        setGoalPeriodStart(null);
        setGoalPeriodEnd(null);
        setMentionedUsers(new Set());
        setDescription('');
      }
    }
  }, [isOpen, editingGoal]);

  const handleClose = () => {
    setTitle('');
    setSelectedModelId('');
    setModelType('');
    setDefineCampaignsManually(false);
    setCampaignGroupId('');
    setSelectedCampaigns(new Set());
    setMetric('');
    setTargetValue('');
    setSelectedMediaChannels(new Set());
    setGoalPeriodStart(null);
    setGoalPeriodEnd(null);
    setMentionedUsers(new Set());
    setDescription('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      showError('Validation Error', 'Title is required');
      return;
    }
    if (!isEditing && !selectedModelId) {
      showError('Validation Error', 'Please select a model');
      return;
    }
    if (!modelType) {
      showError('Validation Error', 'Model type is required');
      return;
    }
    if (!metric) {
      showError('Validation Error', 'Metric is required');
      return;
    }
    const targetNum = parseFloat(targetValue);
    if (!targetValue || isNaN(targetNum) || targetNum <= 0) {
      showError('Validation Error', 'Target value must be a positive number');
      return;
    }
    if (!goalPeriodStart || !goalPeriodEnd) {
      showError('Validation Error', 'Goal period is required');
      return;
    }
    if (goalPeriodEnd < goalPeriodStart) {
      showError('Validation Error', 'End date must be after start date');
      return;
    }
    if (!defineCampaignsManually && !campaignGroupId) {
      showError('Validation Error', 'Select a campaign group');
      return;
    }
    if (defineCampaignsManually && selectedCampaigns.size === 0) {
      showError('Validation Error', 'Select at least one campaign');
      return;
    }

    const payload = {
      title,
      description: description || undefined,
      metric: metric as Metric,
      targetValue: targetNum,
      goalPeriodStart: dayjs(goalPeriodStart).format('YYYY-MM-DD'),
      goalPeriodEnd: dayjs(goalPeriodEnd).format('YYYY-MM-DD'),
      modelType: modelType as ModelType,
      defineCampaignsManually,
      campaignGroupId: campaignGroupId || undefined,
      campaignIds: Array.from(selectedCampaigns),
      mediaChannels: Array.from(selectedMediaChannels),
      mentionedUsers: Array.from(mentionedUsers),
    };

    if (isEditing) {
      try {
        await updateGoalMutation.mutateAsync(payload as UpdateGoalRequest);
        onUpdateSuccess();
      } catch (error) {
        const message = (
          error as { response?: { data?: { message?: string } } }
        )?.response?.data?.message;
        onUpdateError(message);
      }
    } else {
      onCreateSubmit({
        ...payload,
        modelParametersId: selectedModelId,
      } as CreateGoalRequest);
    }
  };

  const isLoading = isEditing ? updateGoalMutation.isLoading : isCreateLoading;

  const usersForSelect = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        name: u.name,
        avatarUrl: u.avatarUrl,
      })),
    [users],
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="32px"
            color="content.title">
            {isEditing ? 'Edit Goal' : 'Create New Goal'}
          </Text>
        </DrawerHeader>

        <DrawerBody p={6} overflowY="auto">
          <VStack align="stretch" spacing={6}>
            {/* Goal Title */}
            <FormControl>
              <FormLabel {...labelStyles}>Goal Title</FormLabel>
              <Input
                placeholder="Enter goal title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                {...inputStyles}
              />
            </FormControl>

            {/* Model Name - Select in create mode, read-only in edit mode */}
            <FormControl>
              <FormLabel {...labelStyles}>Model Name</FormLabel>
              {isEditing ? (
                <Input
                  value={stripFullModelSuffix(editingGoal?.modelName ?? '')}
                  isReadOnly
                  {...inputStyles}
                  bg="surface.sidebarMuted"
                  cursor="not-allowed"
                  _focus={{ boxShadow: 'none', borderColor: 'surface.border' }}
                />
              ) : (
                <Select
                  placeholder="Select model"
                  value={selectedModelId}
                  onChange={(e) => handleModelSelect(e.target.value)}
                  {...inputStyles}>
                  {selectableModelVersions.map((model) => (
                    <option
                      key={model.modelParametersId}
                      value={model.modelParametersId}>
                      {baseModelName(model)} ({model.modelType.toUpperCase()})
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            {/* Model Type (read-only, auto-set from selected model) */}
            <FormControl>
              <FormLabel {...labelStyles}>Model Type</FormLabel>
              <Select
                placeholder={isEditing ? '' : 'Select model first'}
                value={modelType}
                isDisabled
                {...inputStyles}
                bg={isEditing ? 'surface.sidebarMuted' : 'surface.panel'}>
                {MODEL_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Define Campaigns Manually Toggle */}
            <HStack justify="space-between">
              <FormLabel {...labelStyles} mb={0}>
                Define Campaigns Manually
              </FormLabel>
              <Switch
                isChecked={defineCampaignsManually}
                onChange={(e) => setDefineCampaignsManually(e.target.checked)}
                colorScheme="green"
              />
            </HStack>

            {/* Campaign Group Select */}
            {!defineCampaignsManually && (
              <FormControl>
                <FormLabel {...labelStyles}>Campaign Group</FormLabel>
                <Select
                  placeholder="Select campaign group"
                  value={campaignGroupId}
                  onChange={(e) => setCampaignGroupId(e.target.value)}
                  {...inputStyles}>
                  {campaignGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Manual Campaign Selection */}
            {defineCampaignsManually && (
              <CampaignsMultiSelect
                campaigns={campaignsForSelect}
                selectedIds={selectedCampaigns}
                onSelectionChange={setSelectedCampaigns}
              />
            )}

            {/* Metric Select — outcome option comes from the selected model's KPI metric */}
            <FormControl>
              <FormLabel {...labelStyles}>Metric</FormLabel>
              <Select
                placeholder="Select metric"
                value={metric}
                isDisabled={!selectedModel && !isEditing}
                onChange={(e) => setMetric(e.target.value as Metric)}
                {...inputStyles}>
                {metricOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Target Value Input */}
            <FormControl>
              <FormLabel {...labelStyles}>Target Value</FormLabel>
              <Input
                placeholder="Enter target value"
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                {...inputStyles}
              />
            </FormControl>

            {/* Media Channels Multi-Select */}
            <MediaChannelsSelect
              options={modelMedia}
              selectedChannels={selectedMediaChannels}
              onSelectionChange={setSelectedMediaChannels}
            />

            {/* Date Range Picker */}
            <GoalDateRangePicker
              startDate={goalPeriodStart}
              endDate={goalPeriodEnd}
              onChange={(start, end) => {
                setGoalPeriodStart(start);
                setGoalPeriodEnd(end);
              }}
            />

            {/* Mentioned Users Multi-Select */}
            <MentionedUsersSelect
              options={usersForSelect}
              selectedIds={mentionedUsers}
              onSelectionChange={setMentionedUsers}
            />

            {/* Description */}
            <FormControl>
              <FormLabel {...labelStyles}>Description</FormLabel>
              <Textarea
                placeholder="Describe your goal"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                resize="vertical"
                {...inputStyles}
              />
            </FormControl>
          </VStack>
        </DrawerBody>

        <Box p={6} display="flex" gap={4}>
          <Button
            bg="surface.sidebarMuted"
            color="content.label"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-goal"
            fontFamily="marketingPage"
            onClick={handleClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName={isEditing ? 'save-goal' : 'create-goal'}
            fontFamily="marketingPage"
            onClick={handleSubmit}
            flex={1}
            isLoading={isLoading}
            _hover={{ bg: 'green.600' }}>
            {isEditing ? 'Save Changes' : 'Create Goal'}
          </Button>
        </Box>
      </DrawerContainer>

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </Drawer>
  );
};
